import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Check, Plus, BadgeCheck } from "lucide-react";
import { fileUrl, outbound, pricingLabel, formatDate } from "@/lib/api";
import { useCompare } from "@/lib/compare";
import { useSite, categoryName } from "@/lib/site";

export const SoftwareLogo = ({ s, size = "h-12 w-12" }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className={`${size} shrink-0 rounded-xl border border-[#E4EAED] bg-white grid place-items-center overflow-hidden`} style={{ boxShadow: "var(--shadow-sm)" }}>
      {s.logo_url && !broken ? (
        <img src={fileUrl(s.logo_url)} alt={`${s.name} logo`} loading="lazy" onError={() => setBroken(true)} className="h-[55%] w-[55%] object-contain" />
      ) : (
        <span className="font-display font-bold text-[#3F5E7A]">{s.name?.[0]}</span>
      )}
    </div>
  );
};

export const CompareButton = ({ s }) => {
  const { has, toggle } = useCompare();
  const on = has(s.slug);
  return (
    <button onClick={() => toggle(s)} aria-pressed={on} data-testid={`compare-toggle-${s.slug}`}
      className={`btn btn-sm border active:scale-[0.97] ${on ? "bg-[#ECF6F4] border-[#0F766E] text-[#0F766E]" : "bg-white border-[#CBD6DB] text-[#17243A] hover:border-[#3F5E7A]"}`}>
      {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {on ? "Added" : "Compare"}
    </button>
  );
};

export default function SoftwareCard({ s }) {
  const { categories } = useSite();
  const link = outbound(s);
  return (
    <article data-testid={`software-card-${s.slug}`} className="card card-hover group flex flex-col h-full overflow-hidden">
      <div className="p-6 pb-5 flex-1 flex flex-col">
        <div className="flex items-center gap-4">
          <SoftwareLogo s={s} size="h-14 w-14" />
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold leading-tight">
              <Link to={`/software/${s.slug}`} className="hover:text-[#3F5E7A] transition-colors" data-testid={`software-card-title-${s.slug}`}>{s.name}</Link>
            </h3>
            <Link to={`/categories/${s.category}`} className="mt-1 inline-block text-xs font-medium text-[#64728A] hover:text-[#0F766E] transition-colors">{categoryName(categories, s.category)}</Link>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[#526174] line-clamp-2">{s.short_description}</p>
        {s.key_features?.length > 0 && (
          <ul className="mt-4 space-y-1.5" aria-label="Key features">
            {s.key_features.slice(0, 3).map((f) => (
              <li key={f} className="flex items-center gap-2 text-[13px] text-[#3A4A5E]"><Check className="h-3.5 w-3.5 text-[#0F766E] shrink-0" />{f}</li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-5">
        <div className="rounded-xl bg-[#F6F7F9] border border-[#E7EDEF] px-3.5 py-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8A96A8]">Pricing</span>
            {s.pricing_verified_at && s.pricing_summary ? (
              <span className="flex items-center gap-1 text-[#0F766E] font-medium"><BadgeCheck className="h-3.5 w-3.5" />Verified {formatDate(s.pricing_verified_at)}</span>
            ) : s.pricing_checked_at && s.pricing_summary ? (
              <span className="text-[#0F766E] font-medium">Official page · {formatDate(s.pricing_checked_at)}</span>
            ) : null}
          </div>
          <p className="mt-1 font-semibold text-[#17243A]">{pricingLabel(s.pricing_model)}</p>
          <p className="mt-0.5 text-[#64728A] line-clamp-2" data-testid={`software-card-pricing-${s.slug}`}>
            {s.pricing_summary && (s.pricing_verified_at || s.pricing_checked_at) ? s.pricing_summary : "Plan prices not verified — see official website."}
          </p>
        </div>
        </div>
      </div>
      <div className="px-6 py-4 border-t border-[#E7EDEF] bg-[#FBFCFC] flex flex-wrap items-center gap-2">
        <Link to={`/software/${s.slug}`} className="btn-primary btn-sm" data-testid={`software-card-read-review-${s.slug}`}>Read Review</Link>
        <CompareButton s={s} />
        <a {...link} className="ml-auto btn-ghost btn-sm !px-2.5 text-[#3F5E7A]" aria-label={`Visit the official ${s.name} website`} title="Visit official website" data-testid={`software-card-visit-${s.slug}`}>
          Website<ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </article>
  );
}
